import './App.css';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { getBestRendition, getGifHeight } from '@giphy/js-util';
import ImgurComponent from './Imgur';
const keyword_extractor = require("keyword-extractor");

function randomPointInCircularMargin(radiusPercent) {
  const theta = Math.random() * 2 * Math.PI;
  const r = Math.sqrt(Math.random()) * radiusPercent;
  return {
    left: 50 + r * Math.cos(theta),
    top: 50 + r * Math.sin(theta),
  };
}

function stickerDisplaySize(gif, targetWidth) {
  const imgs = gif?.images;
  if (!imgs) return { width: targetWidth, height: targetWidth };

  if (gif?.is_sticker !== false) {
    const meta =
      imgs.fixed_width_small ||
      imgs.downsized_small ||
      imgs.fixed_height_small ||
      imgs.fixed_width ||
      imgs.fixed_height ||
      imgs.downsized_medium ||
      imgs.downsized;
    const w = Math.min(targetWidth, meta?.width || targetWidth);
    if (meta?.width && meta?.height) {
      return { width: w, height: Math.max(1, Math.round((w * meta.height) / meta.width)) };
    }
  }

  let h = getGifHeight(gif, targetWidth);
  const meta =
    imgs.fixed_width ||
    imgs.fixed_height ||
    imgs.downsized_medium ||
    imgs.downsized ||
    imgs.original;
  if (!h || h < 1) {
    if (meta?.width && meta?.height) {
      h = Math.max(1, Math.round((targetWidth * meta.height) / meta.width));
    } else {
      h = targetWidth;
    }
  }
  return { width: targetWidth, height: h };
}

function StickerImg({ gif, width = 100 }) {
  if (!gif?.images) return null;
  const imgs = gif.images;
  const isSticker = gif.is_sticker !== false;
  const tw = isSticker ? Math.min(width, 120) : width;
  const { width: w, height: h } = stickerDisplaySize(gif, tw);
  let url = '';
  if (isSticker) {
    const sm = imgs.fixed_width_small || imgs.fixed_height_small || imgs.downsized_small;
    url =
      (sm?.webp || sm?.url || '').trim() ||
      imgs.fixed_width_small_still?.url ||
      imgs.fixed_height_small_still?.url ||
      '';
  }
  if (!url) {
    try {
      const { renditionName } = getBestRendition(imgs, w, h);
      const r = imgs[renditionName];
      if (r) url = (r.url || r.webp || '').trim();
    } catch (_) {}
  }
  if (!url) {
    url =
      (imgs.downsized_small?.url || '').trim() ||
      imgs.downsized?.url ||
      imgs.fixed_height?.url ||
      imgs.fixed_width?.url ||
      imgs.original?.url ||
      '';
  }
  if (!url) return null;
  return (
    <img
      className="gif-sticker-img"
      src={url}
      width={w}
      height={h}
      alt=""
      draggable={false}
    />
  );
}

const STICKER_API = { type: 'stickers', rating: 'pg-13' };

const RANDOM_STICKER_TAGS = ['funny', 'cute', 'love', 'happy', 'party', 'wow', 'yes', 'nice', 'cool', 'omg', 'hey', 'wave', 'star'];

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function onlyStickerAssets(list) {
  if (!list?.length) return [];
  return list.filter((g) => g && g.is_sticker !== false);
}

function takeUniqueSticker(gifs, seenIds) {
  if (!gifs?.length) return null;
  const shuffled = [...gifs].sort(() => Math.random() - 0.5);
  for (const g of shuffled) {
    const id = String(g?.id ?? '');
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    return g;
  }
  return null;
}

function pickUniqueStickerStrict(gifs, seenIds) {
  return takeUniqueSticker(onlyStickerAssets(gifs), seenIds);
}

const DEMO_NEWS_ARTICLES = [
  {
    title: 'Aurora season may peak with vivid northern displays',
    description:
      'solar wind geomagnetic storm northern lights atmosphere research sky photography winter polar glow brilliant wave science',
    content:
      'Sky watchers say a busier solar cycle could align with clear nights, making aurora hunting unusually rewarding at higher latitudes this season.',
    image: 'https://picsum.photos/seed/blink-demo-aurora/1200/800',
    publishedAt: '2026-05-14T10:00:00.000Z',
  },
  {
    title: 'City planners test car-free weekends in historic districts',
    description:
      'urban design pedestrian street bicycle cafe community traffic pollution walkable neighborhood culture tourism local business',
    content:
      'A handful of downtown corridors will close to motor traffic on Saturdays, aimed at foot traffic, outdoor dining, and quieter air in the core.',
    image: 'https://picsum.photos/seed/blink-demo-city/1200/800',
    publishedAt: '2026-05-14T11:30:00.000Z',
  },
  {
    title: 'Lab reports steady progress on drought-tolerant grain trials',
    description:
      'agriculture crop science farming harvest soil climate resilience drought wheat research rural food security water',
    content:
      'Field trials spanning two growing seasons show modest yield gains under water stress, though breeders caution results vary sharply by region.',
    image: 'https://picsum.photos/seed/blink-demo-farm/1200/800',
    publishedAt: '2026-05-14T13:15:00.000Z',
  },
  {
    title: 'Coastal marshes show surprising carbon uptake after restoration',
    description:
      'wetland ecology climate carbon mangrove salt marsh conservation biodiversity shoreline storm surge habitat nature coastal',
    content:
      'Restored tidal flow is linked to denser vegetation and measurable carbon storage gains within a few years, according to a multi-site study.',
    image: 'https://picsum.photos/seed/blink-demo-marsh/1200/800',
    publishedAt: '2026-05-14T15:45:00.000Z',
  },
  {
    title: 'Independent bookstores report a quiet rebound in membership programs',
    description:
      'books reading literature community indie retail subscription author event culture local shop weekend magazine literary',
    content:
      'Owners say curated picks and small events are drawing repeat visitors, even as overall foot traffic remains uneven from block to block.',
    image: 'https://picsum.photos/seed/blink-demo-books/1200/800',
    publishedAt: '2026-05-14T17:00:00.000Z',
  },
];

const GNEWS_API_KEY = process.env.REACT_APP_GNEWS_API_KEY || '';

const giphyFetch = new GiphyFetch('bvWogBDRALOdICcvDhJPS5XVxe50qs7O');

function dedupeArticles(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const a of list) {
    if (!a) continue;
    const k = `${a.url ?? ''}|${a.publishedAt ?? ''}|${(a.title ?? '').slice(0, 120)}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(a);
  }
  return out;
}

function gnewsErrorsToString(body) {
  if (!body?.errors) return '';
  const e = body.errors;
  if (Array.isArray(e)) return e.filter(Boolean).join('; ');
  if (typeof e === 'object') return Object.values(e).flat().filter(Boolean).join('; ');
  return String(e);
}

function App() {
  const [currentData, setCurrentData] = useState(0)
  const [data, setData] = useState([]);
  const [clickedData, setClickedData] = useState([]);
  const [blink, setBlink] = useState("visible")
  const [noblinking, setBlinking] = useState("none")
  const currentDataRef = useRef(0);
  useEffect(() => {
    currentDataRef.current = currentData;
  }, [currentData]);
  const [startWithImgur] = useState(() => Math.random() < 0.5);
  const seenStickerIdsRef = useRef(new Set())

    useEffect(() => {
      let cancelled = false;

      function fallbackToOfflineDemo(reason) {
        if (cancelled) return;
        setData(DEMO_NEWS_ARTICLES);
        if (reason) console.warn('GNews:', reason);
      }

      async function loadLiveNews() {
        if (!GNEWS_API_KEY) {
          fallbackToOfflineDemo('No REACT_APP_GNEWS_API_KEY');
          return;
        }

        const url =
          'https://gnews.io/api/v4/top-headlines?category=' +
          encodeURIComponent('world') +
          '&lang=en&country=us&max=50&apikey=' +
          encodeURIComponent(GNEWS_API_KEY);

        try {
          const res = await fetch(url);
          let body = null;
          try {
            body = await res.json();
          } catch {
            fallbackToOfflineDemo('Response was not JSON');
            return;
          }

          if (!res.ok) {
            const detail = gnewsErrorsToString(body) || res.statusText;
            fallbackToOfflineDemo(
              `Live fetch failed (${res.status}). ${detail}`.trim()
            );
            return;
          }

          if (body.errors) {
            const errMsg = gnewsErrorsToString(body);
            if (errMsg) {
              fallbackToOfflineDemo(`API errors: ${errMsg}`);
              return;
            }
          }

          const raw = Array.isArray(body?.articles) ? body.articles : [];
          const list = dedupeArticles(raw);
          if (list.length === 0) {
            fallbackToOfflineDemo('No articles in response (empty list).');
            return;
          }

          if (!cancelled) setData(list);
        } catch (e) {
          fallbackToOfflineDemo(`Network or parse error: ${e?.message ?? e}`);
        }
      }

      loadLiveNews();
      return () => {
        cancelled = true;
      };
    }, [])

    const nextData = useCallback(() => {
      if (!data.length) return;
      setBlink("hidden");
      setTimeout(() => {
        setBlink("visible");
        setBlinking("block");
      }, 250);

      setBlinking("none");

      const from = currentDataRef.current;
      setClickedData((ch) => [...ch, from]);
      setCurrentData(from >= data.length - 1 ? 0 : from + 1);
    }, [data.length]);

    useEffect(() => {
      const onClick = () => nextData();
      window.addEventListener('click', onClick, true);
      return () => window.removeEventListener('click', onClick, true);
    }, [nextData]);

    function GifDemo({ placementSeed, title, description, content, stickerPool = 'global' }) {
      const localSeenRef = useRef(new Set());
      const seen =
        stickerPool === 'global' ? seenStickerIdsRef.current : localSeenRef.current;
      const [gif, setGif] = useState(null);
      const stickerPosition = useMemo(
        () => randomPointInCircularMargin(42),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [placementSeed]
      );

      const textForStickers = useMemo(() => {
        const raw = [title, description, content].filter(Boolean).join(' ');
        return raw
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 2500);
      }, [title, description, content]);

      useEffect(() => {
        let cancelled = false;
        const isCancelled = () => cancelled;
        const extracted = keyword_extractor.extract(textForStickers || '', {
          language: 'english',
          remove_digits: true,
          return_changed_case: true,
          return_chained_words: true,
          remove_duplicates: true,
        });
        const termPool =
          extracted && extracted.length > 0
            ? extracted
            : ['news', 'world', 'headlines', 'today'];
        const uniqueTerms = [...new Set(termPool)].sort(() => Math.random() - 0.5);

        async function loadUniqueSticker() {
          for (const term of uniqueTerms) {
            if (isCancelled()) return;
            const limit = 50;
            for (let page = 0; page < 20; page++) {
              if (isCancelled()) return;
              try {
                const { data: gifs } = await giphyFetch.search(term, {
                  ...STICKER_API,
                  sort: 'relevant',
                  lang: 'en',
                  limit,
                  offset: page * limit,
                });
                const picked = pickUniqueStickerStrict(gifs, seen);
                if (picked) {
                  setGif(picked);
                  return;
                }
              } catch (e) {
                console.warn('giphy stickers search', e);
              }
            }
          }

          const trendLimit = 50;
          for (let page = 0; page < 24; page++) {
            if (isCancelled()) return;
            try {
              const { data: gifs } = await giphyFetch.trending({
                ...STICKER_API,
                limit: trendLimit,
                offset: page * trendLimit,
              });
              const picked = pickUniqueStickerStrict(gifs, seen);
              if (picked) {
                setGif(picked);
                return;
              }
            } catch (e) {
              console.warn('giphy stickers trending', e);
            }
          }

          for (let sp = 0; sp < 30; sp++) {
            if (isCancelled()) return;
            try {
              const { data: seeds } = await giphyFetch.trending({
                ...STICKER_API,
                limit: 8,
                offset: sp * 8,
              });
              const stickerSeeds = onlyStickerAssets(seeds);
              const shuffled = [...stickerSeeds].sort(() => Math.random() - 0.5);
              for (const seed of shuffled.slice(0, 4)) {
                if (isCancelled()) return;
                const sid = String(seed?.id ?? '');
                if (!sid) continue;
                try {
                  const { data: related } = await giphyFetch.related(sid, {
                    type: 'stickers',
                    rating: 'pg-13',
                    limit: 50,
                  });
                  const picked = pickUniqueStickerStrict(related, seen);
                  if (picked) {
                    setGif(picked);
                    return;
                  }
                } catch (re) {
                  console.warn('giphy stickers related', re);
                }
              }
            } catch (e) {
              console.warn('giphy stickers related seeds', e);
            }
          }

          for (let attempt = 0; attempt < 100; attempt++) {
            if (isCancelled()) return;
            try {
              const { data } = await giphyFetch.random({
                ...STICKER_API,
                ...(attempt % 4 === 0 ? {} : { tag: pickRandom(RANDOM_STICKER_TAGS) }),
              });
              const id = String(data?.id ?? '');
              if (id && data?.is_sticker !== false && !seen.has(id)) {
                seen.add(id);
                setGif(data);
                return;
              }
            } catch (e) {
              console.warn('giphy stickers random', e);
            }
          }
          if (!isCancelled()) {
            try {
              const { data } = await giphyFetch.random({ ...STICKER_API });
              const id = String(data?.id ?? '');
              if (data?.images && id && data?.is_sticker !== false && !seen.has(id)) {
                seen.add(id);
                setGif(data);
                return;
              }
            } catch (e) {
              console.warn('giphy stickers random last-chance', e);
            }
          }

          if (!isCancelled()) {
            console.warn('giphy: no unique sticker');
          }
        }

        loadUniqueSticker();
        return () => { cancelled = true; };
      }, [placementSeed, textForStickers, seen]);
      if (!gif) return null;
      return (
        <div
          className="gif-sticker-anchor"
          style={{
            left: `${stickerPosition.left}%`,
            top: `${stickerPosition.top}%`,
          }}
        >
          <StickerImg gif={gif} width={100} />
        </div>
      );
    }


  return (
    <div className="App">
      <div className="tabloid">
          <div className="blink-button">
            <button type="button" className="blink-img" aria-label="Advance">
              <img className="blink yesblink" src="eyes.gif" alt="Blinking eye jpg"/>
              <img className="blink noblink" style={{ display: noblinking }} src="eyesopen.jpg" alt="Blinking eye gif"/>
            </button>  
            <div className="shimmer-shell"><p className="shimmer">Click...</p></div>
          </div>
            <div style={{ visibility: blink }} className="answers"> 
              {clickedData.length === 0 &&
                data.length > 0 &&
                data[currentData] &&
                (startWithImgur ? (
                  <>
                    <div className="li" key="lead-imgur">
                      <ImgurComponent />
                    </div>
                    <div
                      className="li"
                      key={`current-${currentData}-${data[currentData].publishedAt ?? ''}-${(data[currentData].title ?? '').slice(0, 40)}`}
                    >
                      <h2 className="newstitle">{data[currentData].title}</h2>
                      <p className="newscontent">{data[currentData].content}</p>
                      <img className="newsimg" style={{left:Math.random()*15 + "%",top:Math.random()*40 + "%"}} src={data[currentData].image} alt="" />
                      <div className="gif">
                        <GifDemo
                          placementSeed={`cur-${currentData}-${data[currentData].publishedAt ?? 'x'}`}
                          title={data[currentData].title}
                          description={data[currentData].description}
                          content={data[currentData].content}
                          stickerPool="global"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    className="li"
                    key={`current-${currentData}-${data[currentData].publishedAt ?? ''}-${(data[currentData].title ?? '').slice(0, 40)}`}
                  >
                    <h2 className="newstitle">{data[currentData].title}</h2>
                    <p className="newscontent">{data[currentData].content}</p>
                    <img className="newsimg" style={{left:Math.random()*15 + "%",top:Math.random()*40 + "%"}} src={data[currentData].image} alt="" />
                    <div className="gif">
                      <GifDemo
                        placementSeed={`cur-${currentData}-${data[currentData].publishedAt ?? 'x'}`}
                        title={data[currentData].title}
                        description={data[currentData].description}
                        content={data[currentData].content}
                        stickerPool="global"
                      />
                    </div>
                  </div>
                ))}
              {clickedData.length > 0 && data.length > 0 && data[currentData] && (
                <div
                  className="li"
                  key={`current-${currentData}-${data[currentData].publishedAt ?? ''}-${(data[currentData].title ?? '').slice(0, 40)}`}
                >
                  <h2 className="newstitle">{data[currentData].title}</h2>
                  <p className="newscontent">{data[currentData].content}</p>
                  <img className="newsimg" style={{left:Math.random()*15 + "%",top:Math.random()*40 + "%"}} src={data[currentData].image} alt="" />
                  <div className="gif">
                    <GifDemo
                      placementSeed={`cur-${currentData}-${data[currentData].publishedAt ?? 'x'}`}
                      title={data[currentData].title}
                      description={data[currentData].description}
                      content={data[currentData].content}
                      stickerPool="global"
                    />
                  </div>
                </div>
              )}
              {Math.random() > 0.4 && clickedData.length > 0 && (
                <div className="li">
                  <ImgurComponent />
                </div>
              )}
              {clickedData.length > 0 &&
                clickedData.map((questionIndex, index) =>
                  Math.random() > 0.3 ? (
                    data[questionIndex] && (
                      <div
                        className="li"
                        key={`hist-article-${index}-${questionIndex}-${data[questionIndex].publishedAt ?? ''}`}
                      >
                        <h2 className="newstitle">{data[questionIndex].title}</h2>
                        <p className="newscontent">{data[questionIndex].content}</p>
                        <img
                          className="newsimg"
                          style={{ left: Math.random() * 15 + '%', top: Math.random() * 40 + '%' }}
                          src={data[questionIndex].image}
                          alt=""
                        />
                        <div className="gif">
                          <GifDemo
                            placementSeed={`hist-${index}-${questionIndex}-${data[questionIndex].publishedAt ?? 'x'}`}
                            title={data[questionIndex].title}
                            description={data[questionIndex].description}
                            content={data[questionIndex].content}
                            stickerPool="local"
                          />
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="li" key={`hist-imgur-${index}`}>
                      <ImgurComponent />
                    </div>
                  )
                )}
              {Math.random() > 0.4 && clickedData.length > 0 && (
                <div className="li">
                  <ImgurComponent />
                </div>
              )}
            </div>
        </div>
    </div>
  );
}

export default App;

